import React, { useMemo, useState } from 'react';

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;
const getItemImage = (item) =>
  item?.priceImage || item?.varietyImage || item?.productImage || 'https://placehold.co/96';

const getFormatDescription = (item) =>
  item?.priceDescription || item?.description || item?.metricUnit || null;

const QuoteItemsTable = ({
  items = [],
  itemsSubtotal,
  orderDiscountAmount,
  totalAmount,
  netTotal,
  taxBreakdown = [],
  excludedTaxLabels = [],
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const totals = useMemo(() => {
    const subtotal =
      typeof itemsSubtotal === 'number'
        ? itemsSubtotal
        : items.reduce((sum, item) => {
            if (!item || item.quantity <= 0) return sum;
            const explicitSubtotal = Number(item.subtotal);
            if (Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0) {
              return sum + Math.floor(explicitSubtotal);
            }
            const unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
            const baseTotal = Math.floor(unit * item.quantity);
            const taxAmount = Number.isFinite(Number(item.taxAmount))
              ? Number(item.taxAmount)
              : null;
            const netAmount = Number.isFinite(Number(item.netAmount))
              ? Number(item.netAmount)
              : taxAmount !== null
                ? baseTotal - taxAmount
                : baseTotal;
            const lineTotal =
              Number.isFinite(Number(item.netAmount)) && taxAmount !== null
                ? netAmount + taxAmount
                : baseTotal;
            return sum + lineTotal;
          }, 0);
    const discount = Number(orderDiscountAmount) || 0;
    const total = Number(totalAmount) || Math.max(subtotal - discount, 0);
    const breakdown = Array.isArray(taxBreakdown) ? taxBreakdown : [];
    const taxTotal = breakdown.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    const net = Number.isFinite(Number(netTotal)) ? Number(netTotal) : Math.max(total - taxTotal, 0);
    return { subtotal, discount, total, net, taxTotal, breakdown };
  }, [items, itemsSubtotal, orderDiscountAmount, totalAmount, netTotal, taxBreakdown]);

  const excludedNote =
    Array.isArray(excludedTaxLabels) && excludedTaxLabels.length > 0
      ? `Impuestos excluidos: ${excludedTaxLabels.join(', ')}`
      : null;

  const resolveItemAmounts = (item) => {
    const qty = Number(item.quantity) || 0;
    const explicitSubtotal = Number(item.subtotal);
    const subtotal = Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0
      ? Math.floor(explicitSubtotal)
      : Math.floor(((Number(item.price) || 0) - (Number(item.discount) || 0)) * qty);
    const unit = qty > 0
      ? subtotal / qty
      : (Number(item.price) || 0) - (Number(item.discount) || 0);
    const taxAmount = Number.isFinite(Number(item.taxAmount)) ? Number(item.taxAmount) : null;
    const netAmount =
      Number.isFinite(Number(item.netAmount))
        ? Number(item.netAmount)
        : taxAmount !== null
          ? subtotal - taxAmount
          : subtotal;
    const totalAmountLine =
      Number.isFinite(Number(item.netAmount)) && taxAmount !== null
        ? netAmount + taxAmount
        : subtotal;
    return { qty, unit, netAmount, totalAmountLine };
  };

  const renderProductSummary = (item) => {
    const productName = item.productName || 'Producto';
    const varietyName = item.varietyName || 'Variedad no especificada';
    const formatDescription = getFormatDescription(item);
    return (
      <div className="flex items-start gap-3">
        <img
          src={getItemImage(item)}
          alt={varietyName || productName}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = 'https://placehold.co/96';
          }}
          className="h-14 w-14 flex-none rounded border border-gray-200 object-cover bg-gray-50"
        />
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 break-words">
            {productName}
          </div>
          <div className="text-xs font-medium text-gray-700 break-words">
            Variedad: {varietyName}
          </div>
          {formatDescription && (
            <div className="text-xs text-gray-500 break-words">
              Formato: {formatDescription}
            </div>
          )}
          {item.discountSource === 'promotion' && Number(item.promotionDiscountAmount || 0) > 0 && (
            <div className="text-xs font-medium text-emerald-700">
              Promo: {item.promotionName || 'Promocion'}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="md:hidden divide-y divide-gray-200">
        {items.map((item, idx) => {
          const { qty, unit, netAmount, totalAmountLine } = resolveItemAmounts(item);
          const isExpanded = expandedIndex === idx;
          return (
            <article key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <button
                type="button"
                onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                className="w-full px-4 py-3 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  {renderProductSummary(item)}
                  <div className="shrink-0 text-right">
                    <p className="text-[11px] text-gray-500">Total</p>
                    <p className="text-sm font-bold text-gray-900">{formatCurrency(totalAmountLine)}</p>
                  </div>
                </div>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-500">Unidad</p>
                    <p className="font-semibold text-gray-900">{item.metricUnit || 'unidad'}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-500">Cantidad</p>
                    <p className="font-semibold text-gray-900">{qty}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-500">Precio</p>
                    <p className="font-semibold text-gray-900">{formatCurrency(unit)}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-500">Valor neto</p>
                    <p className="font-semibold text-gray-900">{formatCurrency(netAmount)}</p>
                  </div>
                </div>
              )}
            </article>
          );
        })}
        <div className="bg-white px-4 py-3 text-sm space-y-1">
          <div className="flex justify-between gap-3"><span>Valor neto</span><span className="font-semibold">{formatCurrency(totals.net)}</span></div>
          {totals.breakdown.map((row) => (
            <div key={row.taxId || row.name} className="flex justify-between gap-3">
              <span>{row.name || 'Impuesto'} {row.rate ? `(${row.rate}%)` : ''}</span>
              <span className="font-semibold">{formatCurrency(row.amount)}</span>
            </div>
          ))}
          {totals.discount > 0 && (
            <div className="flex justify-between gap-3 text-red-600"><span>Descuento</span><span className="font-semibold">-{formatCurrency(totals.discount)}</span></div>
          )}
          <div className="flex justify-between gap-3 text-blue-700"><span className="font-semibold">Total</span><span className="font-bold">{formatCurrency(totals.total)}</span></div>
          {excludedNote && <p className="text-xs text-gray-600">{excludedNote}</p>}
        </div>
      </div>

      <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[280px]">Producto</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor neto</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor total</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item, idx) => {
            const { qty, unit, netAmount, totalAmountLine } = resolveItemAmounts(item);
            return (
              <tr key={idx} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-2 text-sm text-gray-900">
                  {renderProductSummary(item)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">{item.metricUnit || 'unidad'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{qty}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(unit)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-gray-900">{formatCurrency(netAmount)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-gray-900">{formatCurrency(totalAmountLine)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}></td>
            <td className="px-4 py-2 text-right font-semibold text-sm">Valor neto</td>
            <td className="px-4 py-2 text-sm font-semibold">{formatCurrency(totals.net)}</td>
          </tr>
          {totals.breakdown.map((row) => (
            <tr key={row.taxId || row.name}>
              <td colSpan={4}></td>
              <td className="px-4 py-2 text-right font-semibold text-sm">
                {row.name || 'Impuesto'} {row.rate ? `(${row.rate}%)` : ''}
              </td>
              <td className="px-4 py-2 text-sm font-semibold">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
          {excludedNote && (
            <tr>
              <td colSpan={6} className="px-4 py-2 text-xs text-gray-600">
                {excludedNote}
              </td>
            </tr>
          )}
          {totals.discount > 0 && (
            <tr>
              <td colSpan={4}></td>
              <td className="px-4 py-2 text-right font-semibold text-sm text-red-600">Descuento</td>
              <td className="px-4 py-2 text-sm font-semibold text-red-600">-{formatCurrency(totals.discount)}</td>
            </tr>
          )}
          <tr>
            <td colSpan={4}></td>
            <td className="px-4 py-2 text-right font-semibold text-sm">Total</td>
            <td className="px-4 py-2 text-sm font-bold text-blue-700">{formatCurrency(totals.total)}</td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
};

export default QuoteItemsTable;
